import React, { useState, useEffect } from 'react';
import { Container, Typography, Grid, Box, Button, Card, CardMedia, CardContent, CircularProgress, Link } from "@mui/material";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Axios from "axios";

import config, { environment } from '../../config';

function AdvertisementLinks() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);

  const categories = [
    { id: 'LocalProduct', title: t('Advertisement.LocalProduct', 'Helyi termék') },
    { id: 'LocalBusiness', title: t('Advertisement.LocalBusiness', 'Helyi vállalkozás') },
    { id: 'SzeklerlandEvents', title: t('Advertisement.SzeklerlandEvents', 'Székelyföldi esemény') }
  ];

  const getYoutubeThumbnail = (url) => {
    const regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    if (match && match[2].length === 11) {
      return `https://img.youtube.com/vi/${match[2]}/mqdefault.jpg`;
    }
    return 'https://img.youtube.com/vi/fvUnG2jyvOs/mqdefault.jpg'; // Fallback
  };

  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function fetchAds() {
      try {
        const response = await Axios.get(config[environment].advertisementsEndpoint, { cancelToken: source.token });
        setAds(response.data);
        setLoading(false);
      } catch (error) {
        if (!Axios.isCancel(error)) {
          console.error("Error fetching advertisements:", error);
          setLoading(false);
        }
      }
    }
    fetchAds();
    return () => source.cancel();
  }, [config, environment]);

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ height: "100vh" }}>
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Button 
            variant="outlined" 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate('/terkep')}
            sx={{ textTransform: 'none' }}
          >
            {t('Map.BackToMap', 'Vissza a térképhez')}
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'var(--sznodarkblue)' }}>
            {t('Advertisement.TitleShort', 'Reklám')}
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
          {t('Advertisement.Subtitle', 'helyi termékek vállakozások, soron következő események bemutató videói')}
        </Typography>
      </Box>

      {categories.map((cat) => (
        <Box key={cat.id} sx={{ mb: 6 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, borderBottom: '2px solid var(--sznolightblue)', pb: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              {cat.title}
            </Typography>
            {cat.id === 'SzeklerlandEvents' && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<CalendarMonthIcon />}
                onClick={() => navigate('/szekelyfoldi-esemenyek')}
                sx={{ textTransform: 'none' }}
              >
                {t('Advertisement.Calendar', 'Eseménynaptár')}
              </Button>
            )}
          </Box>
          <Grid container spacing={3}>
            {ads.filter(ad => ad.category === cat.id).map((ad) => (
              <Grid item xs={12} sm={6} md={4} key={ad.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.02)' } }}>
                  <a href={ad.youtube_link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <CardMedia
                      component="img"
                      image={getYoutubeThumbnail(ad.youtube_link)}
                      alt={ad.title}
                      sx={{ aspectRatio: '16/9' }}
                    />
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {ad.title}
                      </Typography>
                    </CardContent>
                  </a>
                </Card>
              </Grid>
            ))}
            {ads.filter(ad => ad.category === cat.id).length === 0 && (
              <Grid item xs={12}>
                <Typography variant="body1" color="text.secondary">
                  {t('Advertisement.NoAdsInCategory', 'Ebben a kategóriában jelenleg nincs reklám.')}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      ))}
    </Container>
  );
}

export default AdvertisementLinks;
